import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkles, Save, Trash2, Plus, Share2, Send, Copy, Loader2, FileCode2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import ImageUpload from "@/components/ImageUpload";
import HtmlImportHelp from "@/components/HtmlImportHelp";
import AssessmentFlowCard from "@/components/AssessmentFlowCard";

const emptyQ = {
  title: "", topic: "", description_en: "", description_hi: "", cover_image: "",
  time_per_q_sec: 30, location_ids: [], questions: [], enforce_geofence: true,
  validation_fields: null,  // null => inherit global; [] => only emp_id; ["email","dob"] etc.
  quiz_type_id: null,
  default_phase: "",   // "" | "pre" | "post"
  lock_phase: false,
  // Sequential Assessment Flow (Pre → Training → Post)
  assessment_flow_enabled: false,
  pre_test_max_attempts: 1,
  post_test_max_attempts: 1,
  post_test_window_days: 7,
  training_min_duration_sec: 60,
  training_min_duration_unit: "sec",  // UI-only helper — server stores seconds
  training_asset: null,  // {type:'video|link|ppt|pdf', url:'', storage_path:'', name:''}
  shuffle_questions: false,
  // Per-quiz certificate override — leave empty to use global branding settings.
  cert_override: null,
};
const emptyQuestion = () => ({
  id: crypto.randomUUID(),
  text_en: "", text_hi: "",
  options_en: ["", "", "", ""], options_hi: ["", "", "", ""],
  correct_index: 0,
  correct_indices: [],
  question_type: "single",
  explanation_en: "", explanation_hi: "", points: 10,
  image_path: "",
});

export default function QuestionnaireEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(emptyQ);
  const [locs, setLocs] = useState([]);
  const [quizTypes, setQuizTypes] = useState([]);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiForm, setAiForm] = useState({ topic: "", expectations: "", num_questions: 8, difficulty: "easy" });
  const [aiLoading, setAiLoading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [htmlOpen, setHtmlOpen] = useState(false);
  const [htmlText, setHtmlText] = useState("");
  const [htmlLoading, setHtmlLoading] = useState(false);
  // AI Topic → HTML generator
  const [genOpen, setGenOpen] = useState(false);
  const [genForm, setGenForm] = useState({ topic: "", num_questions: 10, language: "both", mix: "auto", level: "Practical, scenario-based" });
  const [genLoading, setGenLoading] = useState(false);

  useEffect(() => {
    api.get("/masters/locations").then(r => setLocs(r.data));
    api.get("/masters/quiz-types").then(r => setQuizTypes(r.data || []));
    if (id) api.get(`/questionnaires/${id}`).then(r => setQ(r.data));
  }, [id]);

  const isNew = !id;
  const publicLink = q?.token ? `${window.location.origin}/q/${q.token}` : "";
  const qrUrl = q?.token ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(publicLink)}` : "";

  async function save() {
    try {
      if (isNew) {
        const r = await api.post("/questionnaires", q);
        toast.success("Quiz created");
        nav(`/questionnaires/${r.data.id}`);
      } else {
        await api.put(`/questionnaires/${id}`, q);
        toast.success("Saved");
      }
    } catch (e) {
      toast.error("Save failed");
    }
  }

  async function aiGenerate() {
    setAiLoading(true);
    try {
      const startR = await api.post("/jobs/start", { type: "quiz_generate", payload: aiForm });
      const jobId = startR.data.job_id;
      // Poll up to 5 minutes
      let result = null;
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 5000));
        const jr = await api.get(`/jobs/${jobId}`);
        if (jr.data.status === "done") { result = jr.data.result; break; }
        if (jr.data.status === "error") throw new Error(jr.data.error || "Generation failed");
      }
      if (!result) throw new Error("AI generation timed out");
      let newQs = result.questions || [];
      if (q.auto_gen_images) {
        toast.message("Generating banner images… this may take a minute");
        for (let i = 0; i < newQs.length; i++) {
          try {
            const imgStart = await api.post("/jobs/start", { type: "image_generate", payload: { prompt: `Hospital training illustration: ${newQs[i].text_en}` }});
            for (let p = 0; p < 60; p++) {
              await new Promise(r => setTimeout(r, 3000));
              const jr = await api.get(`/jobs/${imgStart.data.job_id}`);
              if (jr.data.status === "done") { newQs[i].image_path = jr.data.result.path; break; }
              if (jr.data.status === "error") break;
            }
          } catch { /* skip */ }
        }
      }
      setQ(prev => ({
        ...prev,
        title: prev.title || `${aiForm.topic} – Training Quiz`,
        topic: prev.topic || aiForm.topic,
        questions: [...(prev.questions || []), ...newQs],
      }));
      toast.success(`Added ${newQs.length} AI-generated questions`);
      setAiOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "AI generation failed");
    } finally {
      setAiLoading(false);
    }
  }

  async function importHtml() {
    if (!htmlText.trim()) { toast.error("Paste HTML or upload a file"); return; }
    setHtmlLoading(true);
    try {
      const startR = await api.post("/jobs/start", { type: "html_import", payload: { html_content: htmlText, language_hint: "auto" }});
      const jobId = startR.data.job_id;
      let result = null;
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 5000));
        const jr = await api.get(`/jobs/${jobId}`);
        if (jr.data.status === "done") { result = jr.data.result; break; }
        if (jr.data.status === "error") throw new Error(jr.data.error || "Import failed");
      }
      if (!result) throw new Error("HTML import timed out");
      setQ(prev => ({ ...prev, questions: [...(prev.questions || []), ...(result.questions || [])] }));
      toast.success(`Imported ${(result.questions || []).length} questions from HTML`);
      setHtmlOpen(false); setHtmlText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "HTML import failed");
    } finally { setHtmlLoading(false); }
  }

  async function generateFromTopic() {
    if (!genForm.topic.trim()) { toast.error("Enter a topic"); return; }
    setGenLoading(true);
    try {
      const startR = await api.post("/jobs/start", { type: "ai_html_generate", payload: genForm });
      const jobId = startR.data.job_id;
      let result = null;
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 5000));
        const jr = await api.get(`/jobs/${jobId}`);
        if (jr.data.status === "done") { result = jr.data.result; break; }
        if (jr.data.status === "error") throw new Error(jr.data.error || "Generation failed");
      }
      if (!result) throw new Error("AI generation timed out");
      setQ(prev => ({ ...prev, questions: [...(prev.questions || []), ...(result.questions || [])] }));
      toast.success(`Generated ${(result.questions || []).length} questions on "${genForm.topic}"`);
      setGenOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "AI generation failed");
    } finally { setGenLoading(false); }
  }

  function updateQ(idx, patch) {
    const next = [...q.questions];
    next[idx] = { ...next[idx], ...patch };
    setQ({ ...q, questions: next });
  }
  function updateOption(idx, oi, key, val) {
    const next = [...q.questions];
    next[idx][key] = [...next[idx][key]];
    next[idx][key][oi] = val;
    setQ({ ...q, questions: next });
  }
  function deleteQ(idx) {
    setQ({ ...q, questions: q.questions.filter((_, i) => i !== idx) });
  }
  function addBlank() {
    setQ({ ...q, questions: [...(q.questions || []), emptyQuestion()] });
  }

  async function onHtmlFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    const txt = await f.text();
    setHtmlText(txt);
  }

  function toggleLocation(lid) {
    const ids = q.location_ids || [];
    if (ids.includes(lid)) setQ({ ...q, location_ids: ids.filter(x => x !== lid) });
    else setQ({ ...q, location_ids: [...ids, lid] });
  }

  return (
    <div className="p-6 lg:p-10 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-sky-600 font-bold">{isNew ? "Create" : "Edit"}</div>
          <h1 className="font-display font-black text-3xl lg:text-4xl text-slate-900 mt-1">Quiz Builder</h1>
          <p className="text-slate-500 mt-1">Author bilingual interactive questions or auto-generate with AI.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Dialog open={genOpen} onOpenChange={setGenOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-xl bg-gradient-to-r from-fuchsia-500 to-pink-500 hover:from-fuchsia-600 hover:to-pink-600 text-white" data-testid="open-ai-gen">
                <Sparkles className="h-4 w-4 mr-1.5"/>AI Topic → Quiz
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-fuchsia-500"/>AI Topic Generator</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="text-sm text-slate-500">Claude writes bilingual MCQs in the platform's HTML format and imports them directly — no copy-paste.</div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Topic *</Label>
                  <Input value={genForm.topic} onChange={(e)=>setGenForm({...genForm, topic: e.target.value})}
                    placeholder="e.g. Infection Control for Nursing Staff" data-testid="ai-gen-topic"/>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs text-slate-500"># Questions</Label>
                    <Input type="number" min="1" max="30" value={genForm.num_questions}
                      onChange={(e)=>setGenForm({...genForm, num_questions: parseInt(e.target.value||10)})} data-testid="ai-gen-count"/>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Language</Label>
                    <Select value={genForm.language} onValueChange={(v)=>setGenForm({...genForm, language: v})}>
                      <SelectTrigger data-testid="ai-gen-lang"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="both">EN + HI</SelectItem>
                        <SelectItem value="en">English only</SelectItem>
                        <SelectItem value="hi">Hindi only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Answer Mix</Label>
                    <Select value={genForm.mix} onValueChange={(v)=>setGenForm({...genForm, mix: v})}>
                      <SelectTrigger data-testid="ai-gen-mix"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto (70/30)</SelectItem>
                        <SelectItem value="single">Single-correct</SelectItem>
                        <SelectItem value="multi">Multi-select</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Level / Style</Label>
                  <Input value={genForm.level} onChange={(e)=>setGenForm({...genForm, level: e.target.value})}
                    placeholder="Practical, scenario-based" data-testid="ai-gen-level"/>
                </div>
                <div className="text-[11px] text-slate-400">Powered by Claude Sonnet. Takes 30-90 seconds for 10 questions.</div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setGenOpen(false)}>Cancel</Button>
                <Button onClick={generateFromTopic} disabled={genLoading || !genForm.topic.trim()}
                  className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="ai-gen-run">
                  {genLoading ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/>Generating…</> : "Generate & Import"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={htmlOpen} onOpenChange={setHtmlOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-xl" data-testid="open-html-import">
                <FileCode2 className="h-4 w-4 mr-1.5"/>Import HTML
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader><DialogTitle className="flex items-center gap-2"><FileCode2 className="h-5 w-5 text-violet-500"/>Import Questions from HTML</DialogTitle></DialogHeader>
              <div className="space-y-3 max-h-[80vh] overflow-y-auto pr-1">
                <div className="text-sm text-slate-500">Upload an .html file or paste HTML markup. The structured parser extracts questions — <b>no AI is required</b>. Bilingual (English + Hindi), embedded images, and correct-answer marks are all supported.</div>
                <HtmlImportHelp />
                <div>
                  <input type="file" accept=".html,.htm,text/html" onChange={onHtmlFile} data-testid="html-file"/>
                </div>
                <Textarea value={htmlText} onChange={e=>setHtmlText(e.target.value)} rows={10}
                  placeholder="<div class='question'><p class='prompt en'>Question…</p><p class='prompt hi'>प्रश्न…</p><ul class='en'>…</ul><ul class='hi'>…</ul></div>"
                  className="font-mono text-xs" data-testid="html-textarea"/>
                <div className="text-xs text-slate-400">Scripts, styles, and event handlers are stripped for security.</div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setHtmlOpen(false)}>Cancel</Button>
                <Button onClick={importHtml} disabled={htmlLoading || !htmlText.trim()} className="bg-violet-600 hover:bg-violet-700" data-testid="html-import-btn">
                  {htmlLoading ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/>Importing…</> : "Import"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={aiOpen} onOpenChange={setAiOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-xl border-sky-300 text-sky-700 hover:bg-sky-50" data-testid="open-ai-gen">
                <Sparkles className="h-4 w-4 mr-1.5"/>Generate with AI
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-sky-500"/>AI Question Generator</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Topic *</Label>
                  <Input value={aiForm.topic} onChange={e=>setAiForm({...aiForm, topic:e.target.value})}
                    data-testid="ai-topic-input"
                    placeholder="e.g. Hand Hygiene & Infection Control"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Focus / Expectations</Label>
                  <Textarea value={aiForm.expectations} onChange={e=>setAiForm({...aiForm, expectations:e.target.value})}
                    placeholder="Cover WHO 5 moments, alcohol % in handrub, etc."/>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500"># Questions</Label>
                    <Input type="number" min={3} max={15} value={aiForm.num_questions} onChange={e=>setAiForm({...aiForm, num_questions: parseInt(e.target.value||8)})}/>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Difficulty</Label>
                    <Select value={aiForm.difficulty} onValueChange={v=>setAiForm({...aiForm, difficulty: v})}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="easy">Easy</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="hard">Hard</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setAiOpen(false)}>Cancel</Button>
                <Button onClick={aiGenerate} disabled={aiLoading || !aiForm.topic}
                  data-testid="ai-generate-btn"
                  className="bg-sky-500 hover:bg-sky-600">
                  {aiLoading ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/>Generating… (20-60s)</> : "Generate"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {!isNew && (
            <Dialog open={shareOpen} onOpenChange={setShareOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-xl" data-testid="open-share">
                  <Share2 className="h-4 w-4 mr-1.5"/>Share / QR
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle>Share this Quiz</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-2xl p-6 grid place-items-center">
                    {qrUrl && <img src={qrUrl} alt="QR" className="rounded-lg border border-slate-200" data-testid="quiz-qr"/>}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Public Link</Label>
                    <div className="flex gap-2">
                      <Input readOnly value={publicLink} data-testid="quiz-public-link"/>
                      <Button variant="outline" size="icon" onClick={()=>{ navigator.clipboard.writeText(publicLink); toast.success("Link copied"); }}>
                        <Copy className="h-4 w-4"/>
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button onClick={async()=>{ await api.post("/assignments/share", { questionnaire_id: q.id, channel:"whatsapp", employee_ids: [] }); toast.success("Shared via WhatsApp (simulated)"); }}
                      className="bg-green-500 hover:bg-green-600">
                      <Send className="h-4 w-4 mr-1.5"/>WhatsApp
                    </Button>
                    <Button onClick={async()=>{ await api.post("/assignments/share", { questionnaire_id: q.id, channel:"email", employee_ids: [] }); toast.success("Shared via Email (simulated)"); }}
                      className="bg-sky-500 hover:bg-sky-600">
                      <Send className="h-4 w-4 mr-1.5"/>Email
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400">WhatsApp & Email sending is mocked — wire real Twilio/Gmail later.</p>
                </div>
              </DialogContent>
            </Dialog>
          )}

          <Button onClick={save} className="bg-sky-500 hover:bg-sky-600 rounded-xl" data-testid="save-quiz-btn">
            <Save className="h-4 w-4 mr-1.5"/>{isNew ? "Create Quiz" : "Save Changes"}
          </Button>
        </div>
      </header>

      <Card className="border-slate-200 rounded-2xl">
        <CardContent className="p-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Title *</Label>
              <Input value={q.title} onChange={e=>setQ({...q, title:e.target.value})}
                data-testid="quiz-title-input"
                placeholder="e.g. Fire Safety in Hospitals"/>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Topic</Label>
                <Input value={q.topic} onChange={e=>setQ({...q, topic:e.target.value})}/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Time / Question (sec)</Label>
                <Input type="number" value={q.time_per_q_sec} onChange={e=>setQ({...q, time_per_q_sec: parseInt(e.target.value||30)})}/>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Quiz Type</Label>
              <Select value={q.quiz_type_id || "none"} onValueChange={(v)=>setQ({...q, quiz_type_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="quiz-type-select"><SelectValue placeholder="None"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {quizTypes.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: t.color }}/>
                        {t.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-400 mt-0.5">Manage types in Masters → Quiz Types.</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Default Phase</Label>
                <Select value={q.default_phase || "none"} onValueChange={(v)=>setQ({...q, default_phase: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="phase-default-select"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    <SelectItem value="pre">Pre-Session</SelectItem>
                    <SelectItem value="post">Post-Session</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-slate-400 mt-0.5">Suggested classification.</div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Employee Override</Label>
                <div className="h-10 flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="phase-lock-wrap">
                    <input type="checkbox" checked={!!q.lock_phase}
                      onChange={(e)=>setQ({...q, lock_phase: e.target.checked})}
                      className="h-4 w-4 accent-sky-600" data-testid="phase-lock"/>
                    <span>Lock — employees can't change it</span>
                  </label>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Description (English)</Label>
              <Textarea value={q.description_en} onChange={e=>setQ({...q, description_en:e.target.value})}/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Description (हिन्दी)</Label>
              <Textarea value={q.description_hi} onChange={e=>setQ({...q, description_hi:e.target.value})}/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Cover Image</Label>
              <ImageUpload
                value={q.cover_image}
                onChange={(p) => setQ({ ...q, cover_image: p })}
                aiHint={q.title ? `Hospital training cover image for: ${q.title}. ${q.topic}` : ""}
                testid="quiz-cover"
              />
            </div>

            <AssessmentFlowCard q={q} setQ={setQ}/>
            <CertificateOverrideCard q={q} setQ={setQ}/>
          </div>
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 p-4 bg-slate-50/60">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-bold text-sm">Geo-fence Lock</div>
                  <div className="text-xs text-slate-500">Restrict to chosen sites</div>
                </div>
                <Switch checked={q.enforce_geofence} onCheckedChange={v=>setQ({...q, enforce_geofence: v})}
                  data-testid="geofence-toggle"/>
              </div>
              <div className="space-y-2 mt-3">
                {locs.length === 0 && <div className="text-xs text-slate-400">No locations yet. Add some in Masters.</div>}
                {locs.map(l => (
                  <label key={l.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={(q.location_ids||[]).includes(l.id)} onChange={()=>toggleLocation(l.id)}/>
                    <span>{l.name}</span>
                    <span className="text-xs text-slate-400 ml-auto">{l.radius_m}m</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-4 bg-slate-50/60">
              <div className="flex items-center justify-between mb-1">
                <div>
                  <div className="font-bold text-sm">Auto-Generate Question Banners</div>
                  <div className="text-xs text-slate-500">Use Nano Banana for each AI question</div>
                </div>
                <Switch checked={!!q.auto_gen_images} onCheckedChange={v=>setQ({...q, auto_gen_images: v})}
                  data-testid="autogen-toggle"/>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-4 bg-slate-50/60" data-testid="quiz-validation-section">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-bold text-sm">Entry Validation</div>
                  <div className="text-xs text-slate-500">What employees must enter to start. Emp ID is always required.</div>
                </div>
                <Switch
                  checked={q.validation_fields !== null && q.validation_fields !== undefined}
                  onCheckedChange={(v)=>setQ({ ...q, validation_fields: v ? [] : null })}
                  data-testid="val-override-toggle"
                />
              </div>
              {q.validation_fields === null || q.validation_fields === undefined ? (
                <div className="text-xs text-slate-400 italic">Using global default (see Branding → Quiz Entry Validation).</div>
              ) : (
                <div className="space-y-1.5 mt-2">
                  {[
                    { key: "email", label: "Email" },
                    { key: "dob", label: "Date of Birth (DD/MM/YYYY)" },
                  ].map(f => (
                    <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(q.validation_fields || []).includes(f.key)}
                        onChange={() => {
                          const cur = new Set(q.validation_fields || []);
                          if (cur.has(f.key)) cur.delete(f.key); else cur.add(f.key);
                          setQ({ ...q, validation_fields: Array.from(cur) });
                        }}
                        data-testid={`quiz-val-${f.key}`}
                      />
                      <span>{f.label}</span>
                    </label>
                  ))}
                  <div className="text-[11px] text-slate-400 pt-1">Only Emp ID will be checked if both are unchecked.</div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-xl">Questions <Badge variant="secondary" className="bg-sky-50 text-sky-700 ml-2">{q.questions?.length || 0}</Badge></h2>
        <Button variant="outline" onClick={addBlank} className="rounded-xl"><Plus className="h-4 w-4 mr-1"/>Add Question</Button>
      </div>

      <div className="space-y-4">
        {(q.questions || []).map((qst, i) => (
          <Card key={qst.id || i} className="border-slate-200 rounded-2xl">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Badge className="bg-sky-100 text-sky-700 border-0">Q{i+1}</Badge>
                  <Select value={qst.question_type || "single"} onValueChange={v => {
                    const patch = { question_type: v };
                    if (v === "single" && qst.question_type !== "single") {
                      patch.correct_index = (qst.correct_indices && qst.correct_indices[0]) ?? qst.correct_index ?? 0;
                      patch.correct_indices = [];
                    } else if (v === "multi" && qst.question_type !== "multi") {
                      patch.correct_indices = qst.correct_index != null ? [qst.correct_index] : [];
                    }
                    updateQ(i, patch);
                  }}>
                    <SelectTrigger className="h-8 w-32 text-xs" data-testid={`qtype-${i}`}><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single answer</SelectItem>
                      <SelectItem value="multi">Multi-select</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Input type="number" value={qst.points} onChange={e=>updateQ(i, { points: parseInt(e.target.value||10) })}
                    className="w-20 text-right" />
                  <span className="text-xs text-slate-500">points</span>
                  <Button variant="ghost" size="sm" onClick={()=>deleteQ(i)}><Trash2 className="h-4 w-4 text-red-500"/></Button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Question (English)</Label>
                  <Textarea value={qst.text_en} onChange={e=>updateQ(i,{ text_en: e.target.value })}/>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">प्रश्न (हिन्दी)</Label>
                  <Textarea value={qst.text_hi} onChange={e=>updateQ(i,{ text_hi: e.target.value })}/>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {[0,1,2,3].map(oi => {
                  const isMulti = (qst.question_type || "single") === "multi";
                  const isCorrect = isMulti
                    ? (qst.correct_indices || []).includes(oi)
                    : qst.correct_index === oi;
                  return (
                  <div key={oi} className={`p-3 rounded-xl border-2 ${isCorrect ? "border-green-300 bg-green-50" : "border-slate-200 bg-white"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-slate-500">Option {String.fromCharCode(65+oi)}</span>
                      <label className="flex items-center gap-1 text-xs text-slate-500 cursor-pointer">
                        {isMulti ? (
                          <input type="checkbox" checked={isCorrect} onChange={()=>{
                            const cur = new Set(qst.correct_indices || []);
                            if (cur.has(oi)) cur.delete(oi); else cur.add(oi);
                            updateQ(i, { correct_indices: Array.from(cur).sort((a,b)=>a-b) });
                          }}/>
                        ) : (
                          <input type="radio" name={`correct-${i}`} checked={isCorrect} onChange={()=>updateQ(i,{ correct_index: oi })}/>
                        )}
                        Correct
                      </label>
                    </div>
                    <Input value={qst.options_en[oi]||""} onChange={e=>updateOption(i, oi, "options_en", e.target.value)} placeholder="English option" className="mb-1.5"/>
                    <Input value={qst.options_hi[oi]||""} onChange={e=>updateOption(i, oi, "options_hi", e.target.value)} placeholder="विकल्प (हिन्दी)"/>
                  </div>
                );})}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Explanation (English)</Label>
                  <Textarea value={qst.explanation_en} onChange={e=>updateQ(i,{ explanation_en: e.target.value })}/>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">व्याख्या (हिन्दी)</Label>
                  <Textarea value={qst.explanation_hi} onChange={e=>updateQ(i,{ explanation_hi: e.target.value })}/>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Question Banner Image (optional)</Label>
                <div className="max-w-sm">
                  <ImageUpload
                    value={qst.image_path}
                    onChange={(p) => updateQ(i, { image_path: p })}
                    aiHint={qst.text_en ? `Hospital training illustration for: ${qst.text_en}` : ""}
                    testid={`qimg-${i}`}
                  />
                </div>
              </div>
              {qst.html_content && (
                <div>
                  <Label className="text-xs text-slate-500 flex items-center justify-between">
                    <span>Rich HTML (from import — shown as-is in quiz)</span>
                    <button type="button" onClick={()=>updateQ(i, { html_content: "" })} className="text-red-500 text-[11px] hover:underline">Clear</button>
                  </Label>
                  <div className="border border-slate-200 rounded-xl p-3 bg-slate-50 max-h-40 overflow-auto text-sm prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: qst.html_content }}/>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {(q.questions || []).length === 0 && (
          <Card className="border-dashed border-2 border-slate-200 rounded-2xl">
            <CardContent className="p-10 text-center text-slate-400">
              No questions yet. Use <span className="font-bold text-sky-600">Generate with AI</span> or click <span className="font-bold">Add Question</span>.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function CertificateOverrideCard({ q, setQ }) {
  const ov = q.cert_override || null;
  const enabled = !!ov;
  const set = (k, v) => setQ({ ...q, cert_override: { ...(ov || {}), [k]: v } });
  return (
    <div className="rounded-2xl border border-slate-200 p-4 bg-slate-50/60" data-testid="cert-override-card">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-bold text-sm">Certificate Override</div>
          <div className="text-xs text-slate-500">Optional — use different template/signatory for this quiz only.</div>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={(v) => setQ({ ...q, cert_override: v ? {} : null })}
          data-testid="cert-override-toggle"
        />
      </div>
      {enabled && (
        <div className="space-y-3 mt-3">
          <div>
            <Label className="text-xs text-slate-500">Template</Label>
            <div className="mt-1 grid grid-cols-3 gap-1.5">
              {["classic", "modern", "elegant"].map(t => (
                <button key={t} type="button" onClick={() => set("cert_template", t)}
                  data-testid={`cert-override-tpl-${t}`}
                  className={`text-xs py-2 rounded-lg border ${(ov?.cert_template || "") === t ? "border-sky-500 bg-sky-50 text-sky-700 font-bold" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Signatory Name</Label>
            <Input value={ov?.cert_signatory_1_name || ""} onChange={e => set("cert_signatory_1_name", e.target.value)}
              placeholder="e.g. Dr. Anita Rao" data-testid="cert-override-sig-name"/>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Signatory Designation</Label>
            <Input value={ov?.cert_signatory_1_designation || ""} onChange={e => set("cert_signatory_1_designation", e.target.value)}
              placeholder="Head of Cardiology"/>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Signature Image</Label>
            <ImageUpload value={ov?.cert_signatory_1_signature_path || ""}
              onChange={(p) => set("cert_signatory_1_signature_path", p)}
              aspect="aspect-[3/1]" testid="cert-override-sig-img"/>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Body Text</Label>
            <Textarea value={ov?.cert_body_text || ""} onChange={e => set("cert_body_text", e.target.value)}
              placeholder="has successfully completed the training {quiz} with a score of {score} on {date}."
              rows={3} className="text-xs font-mono"/>
            <div className="text-[10px] text-slate-400 mt-1">Placeholders: {"{name} {quiz} {score} {date} {emp_id} {designation}"}</div>
          </div>
          <div className="text-[11px] text-slate-500 pt-2 border-t border-slate-200">
            Blank fields fall back to the global branding certificate settings.
          </div>
        </div>
      )}
    </div>
  );
}

